import { useState, useEffect, useRef } from 'react';

const values = [
  {
    id: 'transparency',
    icon: 'ri-eye-line',
    tag: 'Foundation',
    title: 'Radical Transparency',
    body: 'If you can\'t see it, you can\'t control it.\nEvery fee. Every movement. Every dollar—fully visible.',
  },
  {
    id: 'operator',
    icon: 'ri-hand-heart-line',
    tag: 'Philosophy',
    title: 'Built for Operators',
    body: 'We don\'t design for procurement teams.\nWe build for the people responsible for margins, cash flow, and growth.',
  },
  {
    id: 'partnership',
    icon: 'ri-shield-check-line',
    tag: 'Commitment',
    title: 'Embedded Partnership',
    body: 'We don\'t disappear after onboarding.\nWe stay in it with you through integration, optimization, and scale.',
  },
  {
    id: 'clarity',
    icon: 'ri-focus-3-line',
    tag: 'Principle',
    title: 'Clarity Over Complexity',
    body: 'If it needs explaining, it needs simplifying.\nWe remove unnecessary layers so you can actually understand your business.',
  },
  {
    id: 'speed',
    icon: 'ri-flashlight-line',
    tag: 'Execution',
    title: 'Speed That Matters',
    body: 'Faster payouts. Faster answers. Faster execution.\nBecause delays cost more than time, they cost momentum.',
  },
  {
    id: 'scale',
    icon: 'ri-global-line',
    tag: 'Infrastructure',
    title: 'Infrastructure That Adapts',
    body: 'Growth changes everything.\nYour payment system should evolve with it, not hold you back.',
  },
];

const STAGGER_DELAYS = [0, 120, 240, 360, 480, 600];

export default function AboutValues() {
  const [active, setActive] = useState<number | null>(null);
  const [visibleCards, setVisibleCards] = useState<boolean[]>(new Array(values.length).fill(false));
  const [headerVisible, setHeaderVisible] = useState(false);

  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            setHeaderVisible(true);
            values.forEach((_, i) => {
              setTimeout(() => {
                setVisibleCards((prev) => {
                  const next = [...prev];
                  next[i] = true;
                  return next;
                });
              }, 200 + STAGGER_DELAYS[i]);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#F9F9F7] pt-12 mb-[50px]" ref={sectionRef}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-[75px]">

        {/* Section Header */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end mb-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 600ms ease, transform 600ms ease',
          }}
        >
          <div>
            <p
              className="text-[#a8894a] text-[10px] uppercase tracking-[0.25em] font-medium mb-5"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              What We Stand For
            </p>
            <h2
              className="text-[#061e45] text-4xl md:text-5xl lg:text-[54px] font-light leading-[1.1]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How We Actually
              <br />
              <em className="not-italic text-[#a8894a]">Operate.</em>
            </h2>
          </div>
          <blockquote className="border-l-2 border-[#d3b366] pl-6 md:pl-8">
            <p
              className="text-[#061e45]/70 text-[15px] md:text-[17px] lg:text-[19px] leading-relaxed"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
            >
              These are not brand statements. They are how we operate, internally and with every client we work with.
            </p>
          </blockquote>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A]/10">
          {values.map((v, i) => (
            <div
              key={v.id}
              onClick={() => setActive(active === i ? null : i)}
              className={`group p-10 cursor-pointer transition-all duration-300 ${
                active === i ? 'bg-white' : 'bg-[#F9F9F7] hover:bg-white'
              }`}
              style={{
                opacity: visibleCards[i] ? 1 : 0,
                transform: visibleCards[i] ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 600ms ease ${STAGGER_DELAYS[i]}ms, transform 600ms cubic-bezier(0.25,0.46,0.45,0.94) ${STAGGER_DELAYS[i]}ms, background-color 300ms ease`,
              }}
            >
              {/* Icon */}
              <div className="flex items-center justify-between mb-8">
                <div className={`w-14 h-14 flex items-center justify-center rounded-full transition-all duration-300 ${
                  active === i ? 'bg-[#122852]' : 'bg-[#1A1A1A]/6 group-hover:bg-[#122852]/10'
                }`}>
                  <i className={`${v.icon} text-2xl transition-colors duration-300 ${
                    active === i ? 'text-[#d3b366]' : 'text-[#1A1A1A]/35 group-hover:text-[#122852]'
                  }`} />
                </div>
              </div>

              {/* Tag */}
              <p
                className={`text-[10px] uppercase tracking-[0.2em] font-medium mb-3 transition-colors duration-300 ${
                  active === i ? 'text-[#a8894a]' : 'text-[#1A1A1A]/30 group-hover:text-[#a8894a]/70'
                }`}
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                {v.tag}
              </p>

              {/* Title */}
              <h3
                className={`text-xl font-medium leading-snug mb-4 transition-colors duration-300 ${
                  active === i ? 'text-[#061e45]' : 'text-[#1A1A1A]/70 group-hover:text-[#061e45]'
                }`}
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                {v.title}
              </h3>

              {/* Body */}
              <p
                className="text-[#1A1A1A]/50 text-[15px] leading-relaxed mb-6 whitespace-pre-line"
                style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
              >
                {v.body}
              </p>

              {/* Learn more — visible when active */}
              <div
                className="overflow-hidden transition-all duration-300"
                style={{ maxHeight: active === i ? '40px' : '0px', opacity: active === i ? 1 : 0 }}
              >
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium text-[#a8894a] hover:text-[#122852] transition-colors duration-200 cursor-pointer"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Work with us
                  <i className="ri-arrow-right-line text-sm" />
                </a>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
